import Extension from "@/core/interfaces/extension/Extension";
import {ExtensionType, ProgramExtension} from "@/core/interfaces/extension/Extension";
import {Program, ProgramType} from "@/program/interfaces/Program";
import {SubscriptionPlan} from "@/subscription-plan/interfaces";

/**
 * Service for extensions.
 */
const ExtensionService = () => {
    return {
        /**
         * Determine the next action to be taken for an extension based on its current state.
         */
        getNextAction(extension: Extension, subscriptionPlan?: SubscriptionPlan) {
            if (extension.enabled) {
                return {
                    action: 'disable'
                };
            }

            if (
                (extension.stripe_id || extension.subscription_plan)
                && (! (extension.subscribed || extension.latest_active_trial?.status === 'active'))
                && !extension.enabled
                && !subscriptionPlan?.subscribed
            ) {
                return {
                    action: 'buy',
                    prices: extension.subscription_plan
                        ? extension.subscription_plan.prices
                        : extension.prices
                };
            }

            return {
                action: 'enable'
            };
        },

        /**
         * Determine if a given extension is a program.
         *
         * @param extension
         */
        isProgramExtension(extension: Extension): extension is ProgramExtension {
            return extension.extendable_type === ExtensionType.PROGRAM_BUNDLE;
        },

        /**
         * Determine if the given extension is of type support program.
         * @param extension
         */
        isSupportProgramExtension(extension: Extension): extension is ProgramExtension {
            return this.isProgramExtension(extension) && extension.sub_type === ProgramType.SUPPORT;
        },

        /**
         * The support program a module carries.
         *
         * A program module extends a bundle, not a program: its `extendable_id`
         * is the bundle's id, which may well be another program's. The module
         * names its own programmable in `sub_id`: the program patients are
         * targeted by is the bundle's support program with that id.
         *
         * @param extension The module.
         */
        supportProgramOf(extension: Extension | null | undefined): Program | null {
            if (!extension || !this.isProgramExtension(extension)) {
                return null;
            }

            return extension.program_bundle?.programs
                ?.find(program => program.programmable_type === ProgramType.SUPPORT
                    && program.programmable_id === extension.sub_id) ?? null;
        }
    };
};

export default ExtensionService();
